import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    BookOpen,
    Plus,
    Users,
    Search,
    MoreVertical,
    Trash2,
    Edit,
    Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Courses() {
    const { user } = useAuth();
    const [courses, setCourses] = useState([]);
    const [availableCourses, setAvailableCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [creating, setCreating] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        code: ''
    });

    const isInstructor = user.role === 'instructor';
    const isStudent = user.role === 'student';

    const fetchCourses = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/courses`);
            setCourses(response.data);
            
            if (isStudent) {
                const availableRes = await axios.get(`${API}/courses/available`);
                setAvailableCourses(availableRes.data);
            } else {
                setAvailableCourses([]);
            }
        } catch (error) {
            console.error('Error fetching courses:', error);
            toast.error('Failed to load courses');
        } finally {
            setLoading(false);
        }
    }, [isStudent]);

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    const handleCreateCourse = async (e) => {
        e.preventDefault();
        setCreating(true);
        
        try {
            const response = await axios.post(`${API}/courses`, formData);
            setCourses([...courses, response.data]);
            setShowCreateDialog(false);
            setFormData({ title: '', description: '', code: '' });
            toast.success('Course created successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create course');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteCourse = async (courseId) => {
        if (!window.confirm('Are you sure you want to delete this course?')) return;
        
        try {
            await axios.delete(`${API}/courses/${courseId}`);
            setCourses(courses.filter(c => c.id !== courseId));
            toast.success('Course deleted');
        } catch (error) {
            toast.error('Failed to delete course');
        }
    };

    const handleEnroll = async (courseId) => {
        try {
            await axios.post(`${API}/enrollments`, { course_id: courseId });
            await fetchCourses();
            toast.success('Enrolled successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to enroll');
        }
    };

    const filteredCourses = courses.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredAvailable = availableCourses.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-8 w-48 skeleton" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => <div key={i} className="h-48 skeleton rounded-2xl" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="courses-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
                        {isInstructor ? 'My Courses' : 'Courses'}
                    </h1>
                    <p className="text-slate-600 mt-1">
                        {isInstructor 
                            ? 'Manage your courses and content'
                            : isStudent
                                ? 'View courses assigned by your instructor'
                                : 'View assigned courses'
                        }
                    </p>
                </div>
                
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Search courses..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 w-64"
                            data-testid="search-courses-input"
                        />
                    </div>
                    {isInstructor && (
                        <Button
                            onClick={() => setShowCreateDialog(true)}
                            className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full"
                            data-testid="create-course-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            New Course
                        </Button>
                    )}
                </div>
            </div>

            {/* My Courses */}
            <section>
                <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900 mb-4">
                    {isInstructor ? 'Your Courses' : 'Enrolled Courses'}
                </h2>
                
                {filteredCourses.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                        <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                            {isInstructor ? 'No courses created yet' : 'Not enrolled in any courses'}
                        </h3>
                        <p className="text-slate-500 mb-4">
                            {isInstructor 
                                ? 'Create your first course to get started'
                                : 'Your instructor or administrator can add you to a course roster'
                            }
                        </p>
                        {isInstructor && (
                            <Button
                                onClick={() => setShowCreateDialog(true)}
                                className="bg-[#2F8FCB] hover:bg-[#2577a8] rounded-full"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Create Course
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCourses.map((course, index) => (
                            <CourseCard
                                key={course.id}
                                course={course}
                                isInstructor={isInstructor}
                                onDelete={() => handleDeleteCourse(course.id)}
                                index={index}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Available Courses (only when a course explicitly allows self-enrollment) */}
            {isStudent && filteredAvailable.length > 0 && (
                <section>
                    <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900 mb-4">
                        Available Courses
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredAvailable.map((course, index) => (
                            <CourseCard
                                key={course.id}
                                course={course}
                                isInstructor={false}
                                isAvailable={true}
                                onEnroll={() => handleEnroll(course.id)}
                                index={index}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* Create Course Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-['Manrope']">Create New Course</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCourse} className="space-y-4">
                        <div>
                            <Label htmlFor="code">Course Code</Label>
                            <Input
                                id="code"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                placeholder="e.g., ACC101"
                                className="mt-1"
                                required
                                data-testid="course-code-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="title">Course Title</Label>
                            <Input
                                id="title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="e.g., Introduction to Accounting"
                                className="mt-1"
                                required
                                data-testid="course-title-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Describe what students will learn..."
                                className="mt-1"
                                rows={3}
                                required
                                data-testid="course-description-input"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowCreateDialog(false)}
                                className="rounded-full"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={creating}
                                className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full"
                                data-testid="submit-course-btn"
                            >
                                {creating ? 'Creating...' : 'Create Course'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const CourseCard = ({ course, isInstructor, isAvailable, onDelete, onEnroll, index }) => (
    <div
        className={`course-card animate-fade-in stagger-${(index % 5) + 1}`}
        data-testid={`course-card-${course.id}`}
    >
        {/* Header */}
        <div className="h-24 bg-gradient-to-br from-[#0F2A44] to-[#1E3A8A] p-4 flex items-end">
            <span className="text-white/80 text-sm font-medium">{course.code}</span>
        </div>

        {/* Content */}
        <div className="p-5">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 line-clamp-2">{course.title}</h3>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{course.description}</p>
                </div>
                
                {isInstructor && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="p-1 hover:bg-slate-100 rounded-lg" data-testid={`course-menu-${course.id}`}>
                                <MoreVertical className="w-4 h-4 text-slate-400" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                                <Link to={`/courses/${course.id}`} className="flex items-center gap-2">
                                    <Eye className="w-4 h-4" /> View
                                </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="text-red-600 flex items-center gap-2"
                                onClick={onDelete}
                            >
                                <Trash2 className="w-4 h-4" /> Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Users className="w-4 h-4" />
                    <span>{course.enrolled_count} students</span>
                </div>
                
                {isAvailable ? (
                    <Button
                        size="sm"
                        onClick={onEnroll}
                        className="bg-[#2F8FCB] hover:bg-[#2577a8] rounded-full"
                        data-testid={`enroll-btn-${course.id}`}
                    >
                        Enroll
                    </Button>
                ) : (
                    <Link to={`/courses/${course.id}`}>
                        <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            data-testid={`view-course-btn-${course.id}`}
                        >
                            View
                        </Button>
                    </Link>
                )}
            </div>
        </div>
    </div>
);
